/**
 * @fileoverview 쿠키 관리 유틸리티
 *
 * 브라우저 쿠키를 읽고, 쓰고, 삭제하는 함수들을 제공합니다.
 * 쿠키 이름과 값은 자동으로 URL 인코딩/디코딩됩니다.
 */

/**
 * 쿠키 옵션 인터페이스
 */
interface CookieOptions {
  expires?: number | Date | undefined;
  path?: string | undefined;
  domain?: string | undefined;
  secure?: boolean | undefined;
  sameSite?: 'strict' | 'Strict' | 'lax' | 'Lax' | 'none' | 'None' | undefined;
  [property: string]: any;
}

/**
 * 쿠키 값 조회
 *
 * 지정된 이름의 쿠키 값을 가져옵니다.
 * 쿠키 값은 자동으로 URL 디코딩됩니다.
 *
 * @param name - 조회할 쿠키 이름
 * @returns 쿠키 값 (없으면 undefined)
 *
 * @example
 * getCookies('token'); // 'abc123' 또는 undefined
 */
export const getCookies = (name: string): string | undefined => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(?:^|; )${escapedName}=([^;]*)`);
  const matches = document.cookie.match(regex);

  return matches ? decodeURIComponent(matches[1]) : undefined;
};

/**
 * 쿠키 설정
 *
 * 쿠키를 생성하거나 업데이트합니다.
 * 기본적으로 path는 '/'로 설정됩니다.
 * 쿠키 이름과 값은 자동으로 URL 인코딩됩니다.
 *
 * @param name - 쿠키 이름
 * @param value - 쿠키 값
 * @param options - 쿠키 옵션 (expires, path, domain, secure, sameSite 등)
 *
 * @example
 * // 기본 쿠키 설정
 * setCookies('token', 'abc123');
 *
 * // 만료일 포함
 * setCookies('token', 'abc123', { expires: new Date('2024-12-31') });
 *
 * // 보안 쿠키
 * setCookies('token', 'abc123', { secure: true, sameSite: 'strict' });
 */
export const setCookies = (name: string, value: string, options: CookieOptions = {}): void => {
  const cookieOptions: CookieOptions = {
    path: '/',
    ...options,
  };

  let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  Object.entries(cookieOptions).forEach(([optionKey, optionValue]) => {
    const formattedValue =
      optionKey === 'expires' && optionValue instanceof Date
        ? optionValue.toUTCString()
        : optionValue;

    updatedCookie += `; ${optionKey}`;

    if (formattedValue !== true) {
      updatedCookie += `=${formattedValue}`;
    }
  });

  document.cookie = updatedCookie;
};

/**
 * 쿠키 삭제
 *
 * 지정된 이름의 쿠키를 즉시 삭제합니다.
 * 내부적으로 max-age를 -1로 설정하여 삭제합니다.
 *
 * @param name - 삭제할 쿠키 이름
 *
 * @example
 * deleteCookies('token');
 */
export const deleteCookies = (name: string) => {
  setCookies(name, '', {
    'max-age': -1, // max-age를 -1로 설정하면 쿠키가 즉시 삭제됨
  });
};

/**
 * 모든 쿠키 삭제
 *
 * 현재 도메인의 모든 쿠키를 삭제합니다.
 *
 * @example
 * deleteAllCookies();
 */
export const deleteAllCookies = () => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    deleteCookies(name.trim());
  }
};
